<template>
    <Spinner v-if="displaySpinner" />
    <Page>
        <template #title>
            <EditableTitle v-model="businessEntity" icon="pi pi-building" />
        </template>
        <template #controls>
            <LastChangedInfo
                v-if="businessEntity?.identity"
                :entityId="businessEntity.identity"
                :entityType="EntityType.BUSINESS_ENTITY"
            />
            <Button
                v-if="description && !description.content && isEditing"
                v-tooltip.bottom="{
                    value: 'Will generate a first draft of the description based on the context.',
                    showDelay: 100,
                }"
                class="p-button-secondary"
                icon="pi pi-sparkles"
                label="Generate first draft"
                @click="generateBusinessEntityDescription"
            />
        </template>
        <Section editable>
            <TextAppArtefact
                v-if="businessEntity?.identity && description"
                v-model="description"
                :entityId="businessEntity.identity"
                :entityType="EntityType.BUSINESS_ENTITY"
                :isEditing="isEditing"
                :stekzModel="businessEntity"
                ref="textAppArtefact"
            />
        </Section>

        <Section editable>
            <DataTable
                v-if="definition?.content"
                v-model:value="definitionRows"
                :editMode="isEditing ? 'cell' : undefined"
                @cell-edit-complete="onCellEditComplete"
                tableStyle="width: 100%"
            >
                <Column field="name" header="Name" style="width: 200px">
                    <template #editor="{ data, field }">
                        <InputText v-model="data[field]" class="w-full" />
                    </template>
                </Column>
                <Column field="type" header="Type" style="width: 150px">
                    <template #body="{ data }">
                        {{
                            fieldTypes.find((t) => t.value === data.type)
                                ?.label || data.type
                        }}
                    </template>
                    <template #editor="{ data, field }">
                        <Dropdown
                            v-model="data[field]"
                            :options="fieldTypes"
                            optionLabel="label"
                            optionValue="value"
                            class="w-full"
                        />
                    </template>
                </Column>
                <Column field="description" header="Description">
                    <template #editor="{ data, field }">
                        <InputText v-model="data[field]" class="w-full" />
                    </template>
                </Column>
                <Column
                    v-if="isEditing"
                    :exportable="false"
                    style="width: 80px"
                    bodyStyle="text-align: right"
                >
                    <template #body="{ data }">
                        <Button
                            icon="pi pi-trash"
                            outlined
                            severity="danger"
                            @click="removeRow(data)"
                        />
                    </template>
                </Column>
            </DataTable>
            <Button
                v-if="isEditing"
                icon="pi pi-plus"
                label="Add New Field"
                @click="addNewRow"
            />
        </Section>

        <!-- Relationships Section -->
        <Section v-if="outgoingRelationships.length > 0 || isEditing" editable>
            <template #title>Outgoing Relationships</template>
            <DataTable
                ref="outgoingRelationshipsTable"
                :value="outgoingRelationships"
                tableStyle="width: 100%"
            >
                <Column>
                    <template #body="{ data }">
                        <span class="relationship-sentence">
                            <span>
                                {{
                                    getEntityName(
                                        businessEntity?.identity || ""
                                    )
                                }}
                            </span>
                            <span class="relationship-type">{{
                                data.type
                            }}</span>
                            <RouterLink
                                :id="`outgoing-entity-link-${data.other_id}`"
                                class="entity-reference"
                                :to="{
                                    name: 'businessEntityPage',
                                    params: {
                                        orgName: $route.params.orgName,
                                        stekName: $route.params.stekName,
                                        businessEntityId: data.other_id,
                                    },
                                }"
                                >{{ getEntityName(data.other_id) }}</RouterLink
                            >
                        </span>
                    </template>
                </Column>
                <Column
                    v-if="isEditing"
                    :exportable="false"
                    style="width: 80px"
                    bodyStyle="text-align: right"
                >
                    <template #body="{ data }">
                        <Button
                            icon="pi pi-trash"
                            outlined
                            severity="danger"
                            @click="removeRelationship(data)"
                        />
                    </template>
                </Column>
            </DataTable>

            <!-- Add New Relationship Form - Only for Outgoing -->
            <div v-if="isEditing" class="add-relationship-form">
                <div class="form-row">
                    <div class="form-field">
                        <label for="relationshipType">Relationship</label>
                        <Dropdown
                            id="relationshipType"
                            v-model="newRelationship.type"
                            :options="relationshipTypes"
                            optionLabel="label"
                            optionValue="value"
                            placeholder="Select Relationship (or type in a custom one)"
                            class="dropdown-full-width"
                            editable
                        />
                    </div>
                    <div class="form-field">
                        <label for="targetEntity">Related Entity</label>
                        <Dropdown
                            id="targetEntity"
                            v-model="newRelationship.other_id"
                            :options="availableEntities"
                            optionLabel="name"
                            optionValue="identity"
                            placeholder="Select Entity"
                            class="dropdown-full-width"
                        />
                    </div>
                    <Button
                        icon="pi pi-plus"
                        label="Add Relationship"
                        @click="addRelationship"
                        :disabled="!canAddRelationship"
                    />
                </div>
            </div>
        </Section>

        <!-- Incoming Relationships Section -->
        <Section v-if="incomingRelationships.length > 0" editable>
            <template #title>Incoming Relationships</template>
            <DataTable
                ref="incomingRelationshipsTable"
                :value="incomingRelationships"
                tableStyle="width: 100%"
            >
                <Column>
                    <template #body="{ data }">
                        <span class="relationship-sentence">
                            <RouterLink
                                :id="`incoming-entity-link-${data.other_id}`"
                                class="entity-reference"
                                :to="{
                                    name: 'businessEntityPage',
                                    params: {
                                        orgName: $route.params.orgName,
                                        stekName: $route.params.stekName,
                                        businessEntityId: data.other_id,
                                    },
                                }"
                                >{{ getEntityName(data.other_id) }}</RouterLink
                            >
                            <span class="relationship-type">{{
                                data.type
                            }}</span>
                            <span>{{
                                getEntityName(businessEntity?.identity || "")
                            }}</span>
                        </span>
                    </template>
                </Column>
            </DataTable>
        </Section>
    </Page>
    <!-- Outgoing Relationships Popups -->
    <template
        v-for="relationship in outgoingRelationships"
        :key="`outgoing-${relationship.other_id}`"
    >
        <Popup
            v-if="!isEditing && relationship.other_id"
            :anchor="`a#outgoing-entity-link-${relationship.other_id}`"
        >
            <GenericCard
                :entityId="relationship.other_id"
                :entityType="EntityType.BUSINESS_ENTITY"
                :showPath="true"
            />
        </Popup>
    </template>

    <!-- Incoming Relationships Popups -->
    <template
        v-for="relationship in incomingRelationships"
        :key="`incoming-${relationship.other_id}`"
    >
        <Popup
            v-if="!isEditing && relationship.other_id"
            :anchor="`a#incoming-entity-link-${relationship.other_id}`"
        >
            <GenericCard
                :entityId="relationship.other_id"
                :entityType="EntityType.BUSINESS_ENTITY"
                :showPath="true"
            />
        </Popup>
    </template>
</template>

<script setup lang="ts">
import { storeToRefs } from "pinia";
import Button from "primevue/button";
import Column from "primevue/column";
import DataTable, { DataTableCellEditCompleteEvent } from "primevue/datatable";
import Dropdown from "primevue/dropdown";
import InputText from "primevue/inputtext";
import { computed, ref, watch } from "vue";
import { RouterLink } from "vue-router";

import EditableTitle from "@/components/EditableTitle.vue";
import GenericCard from "@/components/EntityInfo/GenericCard.vue";
import LastChangedInfo from "@/components/LastChangedInfo.vue";
import Page from "@/components/Page.vue";
import Popup from "@/components/Popup.vue";
import Section from "@/components/Section.vue";
import TextAppArtefact from "@/components/TextAppArtefact.vue";
import { useEditManager } from "@/composables/edit-manager";
import { useGenerateDescriptionDialog } from "@/composables/generate-description";
import { RelationshipDirection } from "@/lib/openapi/gen";
import Spinner from "@/shared/Spinner.vue";
import {
    BusinessEntity,
    useBusinessEntitiesStore,
} from "@/stores/business-entities-store";
import {
    BusinessEntityRelationship,
    useBusinessEntityManager,
    useBusinessEntityStore,
} from "@/stores/business-entity-store";
import { EntityType } from "@/stores/entity-store";

const { entities } = storeToRefs(useBusinessEntitiesStore());

const { isEditing } = useEditManager();
const { businessEntity, isFetching, description, definition } =
    useBusinessEntityManager();

const {
    addRelationship: addRelationshipToStore,
    removeRelationship: removeRelationshipFromStore,
} = useBusinessEntityStore();
const { generateContent } = useGenerateDescriptionDialog();

const displaySpinner = ref(false);
const outgoingRelationshipsTable = ref();
const incomingRelationshipsTable = ref();

interface DefinitionRow {
    name: string;
    type: string;
    description: string;
}

interface DefinitionField {
    type: string;
    description: string;
}

interface DefinitionContent {
    [key: string]: DefinitionField;
}

const fieldTypes = [
    { label: "String", value: "string" },
    { label: "Number", value: "number" },
    { label: "Integer", value: "integer" },
    { label: "Boolean", value: "boolean" },
];

const definitionRows = computed<DefinitionRow[]>({
    get: () => {
        if (!definition.value?.content) return [];
        try {
            const content: DefinitionContent = JSON.parse(
                definition.value.content
            );
            return Object.entries(content).map(([name, field]) => ({
                name,
                type: field.type,
                description: field.description,
            }));
        } catch {
            return [];
        }
    },
    set: (newValue: DefinitionRow[]) => {
        if (definition.value) {
            const content: DefinitionContent = {};
            newValue.forEach((row) => {
                content[row.name] = {
                    type: row.type,
                    description: row.description,
                };
            });
            definition.value.content = JSON.stringify(content);
        }
    },
});

const onCellEditComplete = (event: DataTableCellEditCompleteEvent) => {
    let { data, newValue, field } = event;
    if (typeof field === "string" && field in data) {
        data[field as keyof DefinitionRow] = newValue;
        if (definition.value) {
            const content: DefinitionContent = {};
            definitionRows.value.forEach((row) => {
                content[row.name] = {
                    type: row.type,
                    description: row.description,
                };
            });
            definition.value.content = JSON.stringify(content);
        }
    }
};

watch(
    isFetching,
    (newValue) => {
        displaySpinner.value = newValue;
    },
    { immediate: true }
);

const generateBusinessEntityDescription = async () => {
    if (!businessEntity.value) {
        return;
    }
    await generateContent(
        businessEntity.value.identity,
        EntityType.BUSINESS_ENTITY,
        description.value?.updateContent.bind(description.value)
    );
};

const addNewRow = () => {
    const newRow: DefinitionRow = {
        name: `field_${definitionRows.value.length + 1}`,
        type: "string",
        description: "",
    };

    if (definition.value) {
        const content: DefinitionContent = {};
        [...definitionRows.value, newRow].forEach((row) => {
            content[row.name] = {
                type: row.type,
                description: row.description,
            };
        });
        definition.value.content = JSON.stringify(content);
    }
};

const removeRow = (rowData: DefinitionRow) => {
    if (definition.value) {
        const content: DefinitionContent = {};
        definitionRows.value
            .filter((row) => row.name !== rowData.name)
            .forEach((row) => {
                content[row.name] = {
                    type: row.type,
                    description: row.description,
                };
            });
        definition.value.content = JSON.stringify(content);
    }
};

const relationships = computed(() => {
    return businessEntity.value?.relationships || [];
});

const outgoingRelationships = computed(() => {
    if (!businessEntity.value?.identity) return [];
    return relationships.value.filter(
        (rel) => rel.direction === RelationshipDirection.OUTGOING
    );
});

const incomingRelationships = computed(() => {
    if (!businessEntity.value?.identity) return [];
    return relationships.value.filter(
        (rel) => rel.direction === RelationshipDirection.INCOMING
    );
});

const newRelationship = ref({
    other_id: null as string | null,
    type: "",
});

// Predefined relationship types
const relationshipTypes = [
    { label: "belongs to", value: "belongs to" },
    { label: "uses", value: "uses" },
    { label: "contains", value: "contains" },
    { label: "has", value: "has" },
    { label: "extends", value: "extends" },
    { label: "implements", value: "implements" },
    { label: "depends on", value: "depends on" },
    { label: "composed of", value: "composed of" },
];

// Computed properties
const canAddRelationship = computed(() => {
    return (
        newRelationship.value.other_id &&
        newRelationship.value.type &&
        newRelationship.value.other_id !== businessEntity.value?.identity
    );
});

const availableEntities = computed(() => {
    if (!businessEntity.value?.identity) return;

    return entities.value.filter(
        (entity: BusinessEntity) =>
            entity.identity !== businessEntity.value?.identity &&
            !outgoingRelationships.value.some(
                (r) => r.other_id === entity.identity
            )
    );
});

const addRelationship = async () => {
    if (!canAddRelationship.value || !businessEntity.value?.identity) return;

    const newRel: BusinessEntityRelationship = {
        other_id: newRelationship.value.other_id!,
        type: newRelationship.value.type,
        direction: RelationshipDirection.OUTGOING,
    };
    console.log("Adding relationship:", newRel);

    addRelationshipToStore(newRel);

    // Reset the form after adding the relationship
    newRelationship.value = {
        other_id: null,
        type: "",
    };
};

const removeRelationship = async (relationship: BusinessEntityRelationship) => {
    // Remove from the list
    removeRelationshipFromStore(relationship.other_id, relationship.direction);
};

const getEntityName = (entityId: string) => {
    const entity = entities.value.find((e) => e.identity === entityId);
    return entity?.name || entityId;
};
</script>

<style scoped lang="scss">
.entity-cell {
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.entity-link {
    text-decoration: none;
}

.relationship-sentence {
    white-space: pre-wrap;
}

.entity-reference {
    color: var(--stekz-primary-color) !important;
    font-weight: bold;
    text-decoration: none;
}

.entity-reference:hover {
    text-decoration: underline;
}

.relationship-type {
    padding: 0 0.5rem;

    font-style: italic;
}

.add-relationship-form {
    margin-top: 1rem;
}

.form-row {
    display: flex;
    gap: 0.5rem;
    align-items: end;
    flex-wrap: wrap;
}

.form-field {
    display: flex;
    flex-direction: column;
    min-width: 200px;
    flex: 1;
}

.form-field label {
    margin-bottom: 0.25rem;
    font-weight: 500;
}

.dropdown-full-width {
    width: 100%;
}
</style>
